import { useState, useEffect, useCallback } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { Mail, Search, ChevronLeft, ChevronRight, Check, X, Minus, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  qualified: 'bg-green-100 text-green-700',
  disqualified: 'bg-red-100 text-red-700',
};

function ConfirmationBadge({ lead }) {
  if (lead.confirmation_email_sent_at) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600" title={`Sent ${new Date(lead.confirmation_email_sent_at).toLocaleString()}`}>
        <Check className="h-3.5 w-3.5" /> Sent
      </span>
    );
  }
  if (lead.confirmation_email_error) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-500" title={lead.confirmation_email_error}>
        <X className="h-3.5 w-3.5" /> {lead.confirmation_email_error === 'rate_limited' ? 'Rate limited' : 'Failed'}
      </span>
    );
  }
  return <span className="inline-flex items-center gap-1 text-xs text-[#B0BEC5]"><Minus className="h-3.5 w-3.5" /></span>;
}

export default function AdminLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [resending, setResending] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getLeads({ page, search, status: statusFilter });
      setLeads(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error('Failed to fetch leads:', err); }
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleStatusChange = async (leadId, status) => {
    try {
      await adminApi.updateLeadStatus(leadId, status);
      fetchLeads();
    } catch (err) { console.error('Failed to update lead status:', err); }
  };

  const handleResendConfirmation = async (lead) => {
    setResending(lead.id);
    try {
      await adminApi.resendConfirmation(lead.id);
      toast.success(`Confirmation re-sent to ${lead.email}`);
      fetchLeads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setResending(null);
  };

  return (
    <AdminLayout title="Leads" breadcrumbs={[{ label: 'Leads' }]}>
      <div className="flex items-center gap-3 mb-4" data-testid="admin-leads-toolbar">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" />
          <input data-testid="admin-leads-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] outline-none" placeholder="Search by email..." />
        </div>
        <select data-testid="admin-leads-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]">
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="qualified">Qualified</option>
          <option value="disqualified">Disqualified</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-leads-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Source</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Submitted</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Confirmation sent</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-[#B0BEC5]">Loading...</td></tr>}
            {!loading && leads.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#B0BEC5]">No leads found</td></tr>}
            {leads.map(lead => (
              <tr key={lead.id} className="border-b border-[#F5F5F5]" data-testid={`admin-lead-row-${lead.email}`}>
                <td className="px-4 py-3 font-medium text-[#0A2533] flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-[#B0BEC5]" /> {lead.email}
                </td>
                <td className="px-4 py-3 text-[#4E6974] text-xs">{lead.source}</td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{new Date(lead.submitted_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <select value={lead.status} onChange={e => handleStatusChange(lead.id, e.target.value)}
                    className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer ${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-600'}`}
                    data-testid={`admin-lead-status-${lead.id}`}>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="disqualified">Disqualified</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  <ConfirmationBadge lead={lead} />
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleResendConfirmation(lead)}
                    disabled={resending === lead.id}
                    className="inline-flex items-center gap-1 text-xs text-[#0097A7] hover:text-[#005F6A] disabled:text-[#B0BEC5] transition-colors"
                    data-testid={`admin-lead-resend-${lead.id}`}
                    title="Resend confirmation email"
                  >
                    <RotateCw className={`h-3.5 w-3.5 ${resending === lead.id ? 'animate-spin' : ''}`} />
                    {resending === lead.id ? 'Sending...' : 'Resend'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8ECF0]">
            <span className="text-xs text-[#4E6974]">{total} leads</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-[#4E6974]">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
